import { useCallback } from 'react';
import { usePageAction } from '@repo/hooks/usePageAction';
import { useElementViewedContext } from '@repo/segment/Analytics/ElementViewed';

type Props = {
  actionSection: string;
  pageIdentifier: string;
  additionalAttributes?: Record<string, string>;
};

export const useFilterEvents = ({ actionSection, pageIdentifier, additionalAttributes }: Props) => {
  const { fireElementInteracted } = useElementViewedContext();

  // TODO: Segment Standardization Cleanup
  const clearAllFiltersPageAction = usePageAction({
    actionName: `${pageIdentifier}_filters_refreshed`,
    actionSection,
    actionType: 'select',
  });

  const clearAllFiltersEvent = useCallback(
    (overrideProps?: Record<string, string | object> | null, callback?: (() => void) | null) => {
      clearAllFiltersPageAction(overrideProps, callback);
      fireElementInteracted('select', {
        entityName: `${pageIdentifier}_filters_refreshed`,
        itemCopy: 'Clear all',
        additionalAttributes: { page_type: 'roundup' },
      });
    },
    [clearAllFiltersPageAction, fireElementInteracted, pageIdentifier],
  );

  // TODO: Segment Standardization Cleanup
  const removeFilterPageAction = usePageAction({
    actionName: `${pageIdentifier}_filters_removed`,
    actionSection,
    actionType: 'select',
    additionalAttributes,
  });

  const removeFilterEvent = useCallback(
    (overrideProps?: Record<string, string | object> | null, callback?: (() => void) | null) => {
      removeFilterPageAction(overrideProps, callback);
      const filterLabel =
        typeof overrideProps?.additional_attributes === 'object'
          ? (overrideProps.additional_attributes as Record<string, string>).filter_cleared
          : undefined;
      fireElementInteracted('select', {
        entityName: `${pageIdentifier}_filters_removed`,
        itemCopy: filterLabel,
        additionalAttributes: { page_type: 'roundup' },
      });
    },
    [removeFilterPageAction, fireElementInteracted, pageIdentifier],
  );

  // TODO: Segment Standardization Cleanup
  const updateFiltersPageAction = usePageAction({
    actionName: `${pageIdentifier}_filters_interacted`,
    actionSection,
    actionType: 'select',
    additionalAttributes,
  });

  const updateFiltersEvent = useCallback(
    (
      overrideProps?: Record<string, string | object> | null,
      callback?: (() => void) | null,
      itemCopy?: string,
    ) => {
      updateFiltersPageAction(overrideProps, callback);
      fireElementInteracted('select', {
        entityName: `${pageIdentifier}_filters_interacted`,
        itemCopy,
        additionalAttributes: {
          // Cast needed because overrideProps is loosely typed for the legacy Page Action.
          // TODO: Segment Standardization Cleanup — replace with a typed filterAttributes param
          ...(overrideProps?.additional_attributes as Record<string, unknown> | undefined),
          page_type: 'roundup',
        },
      });
    },
    [updateFiltersPageAction, fireElementInteracted, pageIdentifier],
  );

  // TODO: Segment Standardization Cleanup
  const sortBySelectPageAction = usePageAction({
    actionName: `${pageIdentifier}_sort_by_interacted`,
    actionSection,
    actionType: 'select',
  });

  const sortBySelectEvent = useCallback(
    (overrideProps?: Record<string, string | object> | null, callback?: (() => void) | null) => {
      sortBySelectPageAction(overrideProps, callback);
      const sortLabel =
        typeof overrideProps?.additional_attributes === 'object'
          ? (overrideProps.additional_attributes as Record<string, string>).sort_by_value
          : undefined;
      fireElementInteracted('select', {
        entityName: `${pageIdentifier}_sort_by_interacted`,
        itemCopy: sortLabel,
        additionalAttributes: { sort_by_value: sortLabel, page_type: 'roundup' },
      });
    },
    [sortBySelectPageAction, fireElementInteracted, pageIdentifier],
  );

  // TODO: Segment Standardization Cleanup
  const showMorePageAction = usePageAction({
    actionName: 'see_more_interacted',
    actionSection,
    actionType: 'select',
  });

  const showMoreButtonClickEvent = useCallback(
    (overrideProps?: Record<string, string | object> | null, callback?: (() => void) | null) => {
      showMorePageAction(overrideProps, callback);
      fireElementInteracted('select', {
        entityName: 'see_more_interacted',
        itemCopy: 'See more rates',
        additionalAttributes: { page_type: 'roundup' },
      });
    },
    [showMorePageAction, fireElementInteracted],
  );

  return {
    clearAllFiltersEvent,
    removeFilterEvent,
    showMoreButtonClickEvent,
    sortBySelectEvent,
    updateFiltersEvent,
  };
};
